import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import MyLayout from '../components/MyLayout';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { fetchMyQnA, submitQuestion } from '@/lib/myAccount';
import type { QnAItem } from '@/types/auth';

const CATEGORIES = ['General', 'Product', 'Shipping', 'Order'];

export default function MyQnAPage() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [qnaList, setQnaList] = useState<QnAItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('General');
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    const rows = await fetchMyQnA(currentUser.id);
    setQnaList(rows);
    setLoading(false);
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentUser || !question.trim()) return;
    setSubmitting(true);
    try {
      await submitQuestion(currentUser.id, category, question.trim());
      setQuestion('');
      showToast(t('my.qna.submitted', 'Your question has been sent — we will reply here soon.'));
      await load();
    } catch {
      showToast(t('my.qna.submitError', 'Could not send your question. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MyLayout title={t('my.qna.title', 'Q&A')}>
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#E8E8E6] p-5 mb-6 flex flex-col gap-3">
        <h4 className="text-sm font-medium text-[#2A2A2A]">{t('my.qna.askTitle', 'Ask a question')}</h4>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full sm:w-48 px-3 py-2 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] focus:outline-none focus:border-[#7B1113]"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('my.qna.placeholder', 'Type your question…')}
          rows={3}
          required
          className="w-full px-3 py-2 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] resize-none"
        />
        <button
          type="submit"
          disabled={submitting || !question.trim()}
          className="self-end px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 cursor-pointer"
        >
          {submitting ? t('common.loading') : t('my.qna.submit', 'Submit')}
        </button>
      </form>

      <div className="bg-white rounded-2xl border border-[#E8E8E6] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-[#8A8A8A]">{t('common.loading')}</div>
        ) : qnaList.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#8A8A8A]">
            {t('my.qna.empty', 'No questions yet')}
          </div>
        ) : (
          <div className="flex flex-col">
            {qnaList.map((qna) => (
              <div key={qna.id} className="p-5 border-b border-[#E8E8E6] last:border-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-xs text-[#8A8A8A] uppercase tracking-wider">
                    {qna.channel}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      qna.status === 'answered'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#F0EFED] text-[#8A8A8A]'
                    }`}
                  >
                    {qna.status === 'answered'
                      ? t('my.qna.answered', 'Answered')
                      : t('my.qna.pending', 'Pending')}
                  </span>
                </div>
                <h4 className="text-sm font-medium text-[#2A2A2A] mb-1">{qna.title}</h4>
                <p className="text-xs text-[#8A8A8A]">{qna.createdAt}</p>
                {qna.answer && (
                  <div className="mt-3 p-3 rounded-xl bg-[#F0EFED] text-sm text-[#2A2A2A]">
                    {qna.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#F0EFED] rounded-2xl p-6 border border-dashed border-[#E8E8E6] mt-6">
        <p className="text-sm text-[#8A8A8A] text-center">
          {t('my.qna.formHint', 'Have an urgent issue? Contact us directly for faster support.')}
        </p>
      </div>
    </MyLayout>
  );
}
